import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import StaffLoginForm from './StaffLoginForm';

const AuthForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, signUp, isAuthenticated, getDefaultRoute } = useAuth();
  const [isSignUp, setIsSignUp] = useState(false);
  const [isStaffLogin, setIsStaffLogin] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    role: 'attendee',
    rememberMe: false
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const from = location?.state?.from?.pathname || getDefaultRoute();
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location, getDefaultRoute]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target || {};
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (isSignUp) {
      if (!formData?.firstName?.trim()) {
        newErrors.firstName = 'First name is required';
      }
      if (!formData?.lastName?.trim()) {
        newErrors.lastName = 'Last name is required';
      }
      if (!formData?.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (formData?.password !== formData?.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
      if (!formData?.role) {
        newErrors.role = 'Please select a role';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    try {
      if (isSignUp) {
        const { user, error, needsConfirmation, message, isEmailError, isExistingUser, canTrySignIn } = await signUp(
          formData?.email,
          formData?.password,
          {
            firstName: formData?.firstName?.trim(),
            lastName: formData?.lastName?.trim(),
            full_name: `${formData?.firstName?.trim()} ${formData?.lastName?.trim()}`,
            role: formData?.role
          }
        );

        if (error) {
          // Enhanced error handling with better user guidance
          if (isExistingUser) {
            setErrors({ 
              general: error,
              switchToSignIn: true
            });
            // Automatically switch to sign-in mode after a delay
            setTimeout(() => {
              setIsSignUp(false);
              setFormData(prev => ({ ...prev, confirmPassword: '', firstName: '', lastName: '', role: 'attendee' }));
              setErrors({});
            }, 3000);
          } else if (isEmailError) {
            setErrors({ 
              general: error,
              showEmailHelp: true,
              canTrySignIn: canTrySignIn || false
            });
          } else {
            setErrors({ general: error });
          }
          return;
        }

        // Show appropriate success message based on confirmation status
        if (needsConfirmation) {
          setErrors({ 
            general: message,
            success: true,
            showEmailCheck: true
          });
        } else {
          setErrors({ 
            general: message,
            success: true
          });
          
          // Auto-redirect after successful account creation without confirmation
          setTimeout(() => {
            const redirectPath = location?.state?.from?.pathname || getDefaultRoute();
            navigate(redirectPath, { replace: true });
          }, 2000);
        }
      } else {
        const { user, error } = await signIn(formData?.email, formData?.password);

        if (error) {
          setErrors({ general: error });
          return;
        }

        // Navigation will be handled by the useEffect above
      }
    } catch (error) {
      setErrors({ general: 'An unexpected error occurred. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const toggleAuthMode = () => {
    setIsSignUp(!isSignUp);
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      role: 'attendee',
      rememberMe: false
    });
    setErrors({});
  };

  const switchToStaffLogin = () => {
    setIsStaffLogin(true);
    setErrors({});
  };

  const switchToRegularLogin = () => {
    setIsStaffLogin(false);
    setIsSignUp(false);
    setErrors({});
  };

  // Show staff login form
  if (isStaffLogin) {
    return <StaffLoginForm onSwitchToRegular={switchToRegularLogin} />;
  }

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-card border border-border rounded-lg shadow-lg p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center w-16 h-16 bg-primary rounded-full mx-auto mb-4">
            <Icon name="Calendar" size={32} color="white" />
          </div>
          <h1 className="text-2xl font-semibold text-foreground mb-2">
            {isSignUp ? 'Create Account' : 'Welcome Back'}
          </h1>
          <p className="text-muted-foreground">
            {isSignUp 
              ? 'Join EventGrid to discover amazing events' :'Sign in to your EventGrid account'
            }
          </p>
        </div>

        {/* Error/Success Message */}
        {errors?.general && (
          <div className={`mb-6 p-4 rounded-lg border ${
            errors?.success 
              ? 'bg-green-50 border-green-200 text-green-800' 
              : errors?.showEmailHelp
              ? 'bg-yellow-50 border-yellow-200 text-yellow-800' :'bg-error/10 border-error/20 text-error'
          }`}>
            <div className="flex items-start space-x-2">
              <Icon 
                name={
                  errors?.success ? "CheckCircle" : errors?.showEmailHelp ?"AlertTriangle": "AlertCircle"
                } 
                size={16} 
                className={`mt-0.5 ${
                  errors?.success ? 'text-green-600' : errors?.showEmailHelp ?'text-yellow-600': 'text-error'
                }`} 
              />
              <div className="flex-1">
                <p className="text-sm">{errors?.general}</p>
                
                {/* Additional help for email errors */}
                {errors?.showEmailHelp && (
                  <div className="mt-2 space-y-2">
                    <p className="text-xs font-medium">Recommended steps:</p>
                    <ul className="text-xs space-y-1 ml-4 list-disc">
                      {errors?.canTrySignIn && (
                        <li className="font-medium text-green-700">Try signing in first - your account may already exist</li>
                      )}
                      <li>Check your spam/junk folder for any confirmation emails</li>
                      <li>Wait a few minutes and try the registration process again</li>
                      <li>Contact support if the issue persists - there may be an email service configuration issue</li>
                    </ul>
                    
                    {errors?.canTrySignIn && (
                      <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs">
                        <p className="font-medium text-blue-800">💡 Quick Fix</p>
                        <p className="text-blue-700">Try clicking "Sign In" below and use the same email/password you just entered.</p>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Show email check reminder */}
                {errors?.showEmailCheck && (
                  <div className="mt-2 p-2 bg-green-100 rounded text-xs">
                    <p className="font-medium">📧 Check your email</p>
                    <p>We sent a confirmation link to <strong>{formData?.email}</strong></p>
                    <p>Click the link in the email to activate your account.</p>
                    <p className="mt-1 text-gray-600">
                      <strong>Note:</strong> If you don't see the email, check your spam folder. 
                      Welcome emails are also sent and may take a few minutes to arrive.
                    </p>
                  </div>
                )}
                
                {/* Auto switch to sign-in notice */}
                {errors?.switchToSignIn && (
                  <div className="mt-2 text-xs">
                    <p>Switching to sign-in mode in a moment...</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {isSignUp && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="First Name"
                  type="text"
                  name="firstName"
                  placeholder="John"
                  value={formData?.firstName || ''}
                  onChange={handleInputChange}
                  error={errors?.firstName}
                  required
                />
                <Input
                  label="Last Name"
                  type="text"
                  name="lastName"
                  placeholder="Doe"
                  value={formData?.lastName || ''}
                  onChange={handleInputChange}
                  error={errors?.lastName}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  I want to join as
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <label className="flex items-center space-x-3 p-4 border border-border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                    <input
                      type="radio"
                      name="role"
                      value="attendee"
                      checked={formData?.role === 'attendee'}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-primary"
                    />
                    <div>
                      <div className="font-medium">Attendee</div>
                      <div className="text-xs text-muted-foreground">Discover and book events</div>
                    </div>
                  </label>
                  <label className="flex items-center space-x-3 p-4 border border-border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                    <input
                      type="radio"
                      name="role"
                      value="host"
                      checked={formData?.role === 'host'}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-primary"
                    />
                    <div>
                      <div className="font-medium">Host</div>
                      <div className="text-xs text-muted-foreground">Create and manage events</div>
                    </div>
                  </label>
                </div>
                {errors?.role && (
                  <p className="mt-2 text-sm text-error">{errors?.role}</p>
                )}
              </div>
            </>
          )}

          <Input
            label="Email Address"
            type="email"
            name="email"
            placeholder="you@example.com"
            value={formData?.email || ''}
            onChange={handleInputChange}
            error={errors?.email}
            required
          />

          <Input
            label="Password"
            type="password"
            name="password"
            placeholder="Enter your password"
            value={formData?.password || ''}
            onChange={handleInputChange}
            error={errors?.password}
            required
          />

          {isSignUp && (
            <Input
              label="Confirm Password"
              type="password"
              name="confirmPassword"
              placeholder="Confirm your password"
              value={formData?.confirmPassword || ''}
              onChange={handleInputChange}
              error={errors?.confirmPassword}
              required
            />
          )}

          {!isSignUp && (
            <div className="flex items-center justify-between">
              <Checkbox
                label="Remember me"
                name="rememberMe"
                checked={formData?.rememberMe || false}
                onChange={handleInputChange}
              />
              <button
                type="button"
                className="text-sm text-primary hover:text-primary/80 transition-colors"
              >
                Forgot password?
              </button>
            </div>
          )}

          <Button
            type="submit"
            variant="default"
            size="lg"
            fullWidth
            loading={isLoading}
            iconName={isSignUp ? "UserPlus" : "LogIn"}
            iconPosition="left"
          >
            {isSignUp ? 'Create Account' : 'Sign In'}
          </Button>
        </form>

        {/* Toggle Auth Mode */}
        <div className="mt-8 text-center">
          <p className="text-muted-foreground">
            {isSignUp ? 'Already have an account?' : "Don't have an account?"}
            <button
              type="button"
              onClick={toggleAuthMode}
              className="ml-2 text-primary hover:text-primary/80 font-medium transition-colors"
            >
              {isSignUp ? 'Sign In' : 'Create Account'}
            </button>
          </p>
        </div>

        {/* Staff Login Link */}
        {!isSignUp && (
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={switchToStaffLogin}
              className="text-green-600 hover:text-green-700 font-medium transition-colors text-sm flex items-center justify-center space-x-2"
            >
              <Icon name="UserCheck" size={16} />
              <span>Staff Login</span>
            </button>
          </div>
        )}

        {/* Social Login */}
        <div className="mt-8">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-card text-muted-foreground">Or continue with</span>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-3">
            <Button
              variant="outline"
              size="default"
              iconName="Mail"
              iconPosition="left"
              disabled={isLoading}
            >
              Google
            </Button>
            <Button
              variant="outline"
              size="default"
              iconName="Github"
              iconPosition="left"
              disabled={isLoading}
            >
              GitHub
            </Button>
          </div>
        </div>
      </div>
      {/* Demo Credentials */}
      <div className="mt-6 p-4 bg-muted/50 rounded-lg border border-border">
        <h3 className="text-sm font-medium text-foreground mb-2">Demo Credentials:</h3>
        <div className="space-y-1 text-xs text-muted-foreground">
          <p><strong>Admin:</strong> admin@eventgrid.com / admin123</p>
          <p><strong>Attendee:</strong> user@eventgrid.com / user123</p>
          <p><strong>Host:</strong> host@eventgrid.com / host123</p>
        </div>
        <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded text-xs">
          <p className="font-medium text-green-800">✅ Authentication Fixed</p>
          <p className="text-green-700">Admin user created and email confirmation errors resolved</p>
        </div>
      </div>
    </div>
  );
};

export default AuthForm;